import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Link,
  Progress,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight, FiExternalLink, FiShield } from "react-icons/fi";
import {
  BuyerPublicBidEspdAnswerDto,
  EspdResponseDataType,
  type BuyerPublicBidDetailDto,
  type BuyerPublicBidEspdCategoryDto,
  type BuyerPublicBidEspdCriterionDto,
  type BuyerPublicBidEspdRequirementDto,
  type BuyerPublicBidEspdRequirementGroupDto,
  type BuyerPublicBidEspdRequirementValueDto,
} from "../../../../autogen/bff-api";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";

type Tone = "ok" | "issue" | "neutral";

interface Props {
  detail: BuyerPublicBidDetailDto;
}

export const EspdTab = ({ detail }: Props) => {
  const { t } = useTranslation();
  const espd = detail.espd;
  const categories = useMemo(() => espd?.categories ?? [], [espd?.categories]);
  const defaultExpandedCategories = useMemo(
    () => categories.filter((category) => categoryHasIssue(category)).map((category) => category.code),
    [categories],
  );
  const defaultExpandedCriteria = useMemo(
    () =>
      categories.flatMap((category) =>
        category.criteria
          .filter((criterion) => criterion.answer !== BuyerPublicBidEspdAnswerDto.Ok)
          .map((criterion) => criterionKey(category, criterion)),
      ),
    [categories],
  );
  const [expandedCategories, setExpandedCategories] = useState<string[]>(() => defaultExpandedCategories);
  const [expandedCriteria, setExpandedCriteria] = useState<string[]>(() => defaultExpandedCriteria);

  useEffect(() => {
    setExpandedCategories(defaultExpandedCategories);
    setExpandedCriteria(defaultExpandedCriteria);
  }, [defaultExpandedCategories, defaultExpandedCriteria]);

  if (!espd?.hasResponse) {
    return <EspdEmptyState />;
  }

  const { label: totalStatus, tone: totalTone } = espdStatus(espd.deviationCount, espd.unansweredCount, t);

  const toggleCategory = (code: string) => {
    setExpandedCategories((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
    );
  };

  const toggleCriterion = (key: string) => {
    setExpandedCriteria((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  };

  const focusCategory = (category: BuyerPublicBidEspdCategoryDto) => {
    const targetCriterion = firstIssue(category);

    setExpandedCategories((current) => addOnce(current, category.code));
    if (targetCriterion) {
      setExpandedCriteria((current) => addOnce(current, criterionKey(category, targetCriterion)));
    }
    scrollToElement(categoryButtonId(category));
  };

  return (
    <Stack spacing="5">
      <SectionCard icon={FiShield} title={t("ESPD Part III")}>
        <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap="4" mb="4" flexWrap="wrap">
          <Text color="smMuted" fontSize="sm" maxW="72ch">
            {t("Exclusion grounds from the supplier's submitted ESPD response.")}
          </Text>
          <StatusPill tone={totalTone}>{totalStatus}</StatusPill>
        </Flex>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="3">
          {categories.map((category) => (
            <CategorySummaryButton key={category.code} category={category} onClick={() => focusCategory(category)} />
          ))}
        </SimpleGrid>
      </SectionCard>

      <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap="3" flexWrap="wrap">
        <Box>
          <Text fontWeight="bold" color="smText">
            {t("Detailed responses")}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t("Expand a question to inspect the supplier's submitted answers.")}
          </Text>
        </Box>
        <Text fontSize="sm" color="smMuted">
          {t("Read-only")}
        </Text>
      </Flex>

      <Stack spacing="3">
        {categories.map((category) => {
          const isOpen = expandedCategories.includes(category.code);
          return (
            <CategorySection
              key={category.code}
              category={category}
              isOpen={isOpen}
              onToggle={() => toggleCategory(category.code)}
            >
              {category.criteria.map((criterion) => {
                const key = criterionKey(category, criterion);
                return (
                  <CriterionSection
                    key={key}
                    category={category}
                    criterion={criterion}
                    isOpen={expandedCriteria.includes(key)}
                    onToggle={() => toggleCriterion(key)}
                  />
                );
              })}
            </CategorySection>
          );
        })}
      </Stack>
    </Stack>
  );
};

const EspdEmptyState = () => {
  const { t } = useTranslation();
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="2"
      py="16"
      rounded="xl"
      bg="smBackground"
      border="1px dashed"
      borderColor="smBorder"
      color="smMuted"
    >
      <Icon as={FiAlertTriangle} boxSize="6" />
      <Text fontWeight="bold" color="smText">
        {t("No ESPD response")}
      </Text>
      <Text fontSize="sm">{t("The supplier has not submitted an ESPD response for this bid.")}</Text>
    </Flex>
  );
};

const CategorySummaryButton = ({
  category,
  onClick,
}: {
  category: BuyerPublicBidEspdCategoryDto;
  onClick: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const { label: status, tone } = espdStatus(category.deviationCount, category.unansweredCount, t);
  const categoryName = espdCategoryName(category, t);
  const issue = firstIssue(category);
  const firstIssueLabel = issue
    ? localizedText(i18n.language, issue.nameSource, issue.nameTranslation, issue.name) || null
    : null;
  const meta = firstIssueLabel ?? t("espd_question_count", { count: category.criteria.length });

  return (
    <Button
      type="button"
      variant="unstyled"
      h="auto"
      minH="88px"
      display="grid"
      alignContent="space-between"
      alignItems="stretch"
      justifyContent="stretch"
      justifyItems="stretch"
      textAlign="left"
      whiteSpace="normal"
      border="1px solid"
      borderColor={toneBorder(tone)}
      bg={toneBg(tone)}
      borderRadius="md"
      p="3"
      onClick={onClick}
      _hover={{ borderColor: toneAccent(tone), transform: "translateY(-1px)" }}
      _focusVisible={{ boxShadow: "smFocusRing" }}
    >
      <Box minW="0">
        <Text fontWeight="bold" color="smText" noOfLines={2} overflowWrap="anywhere">
          {categoryName}
        </Text>
        <Text mt="0.5" fontSize="sm" color="smMuted" noOfLines={1} overflowWrap="anywhere">
          {meta}
        </Text>
      </Box>
      <Text fontSize="sm" fontWeight="bold" color={toneText(tone)}>
        {status}
      </Text>
    </Button>
  );
};

const CategorySection = ({
  category,
  isOpen,
  onToggle,
  children,
}: {
  category: BuyerPublicBidEspdCategoryDto;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const { label: status, tone } = espdStatus(category.deviationCount, category.unansweredCount, t);
  const categoryName = espdCategoryName(category, t);
  const buttonId = categoryButtonId(category);
  const panelId = categoryPanelId(category);

  return (
    <Box
      id={categoryElementId(category)}
      bg="smEspdCategoryBg"
      border="1px solid"
      borderColor="smBorder"
      borderRadius="md"
      overflow="hidden"
    >
      <Button
        id={buttonId}
        type="button"
        variant="unstyled"
        h="auto"
        w="full"
        display="block"
        textAlign="left"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <Flex
          align={{ base: "flex-start", sm: "center" }}
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          gap="3"
          px={{ base: "3", md: "4" }}
          py="3"
          bg="smEspdCategoryBg"
        >
          <Box minW="0">
            <Text fontWeight="bold" color="smText" noOfLines={2} overflowWrap="anywhere">
              {categoryName}
            </Text>
            <Text fontSize="sm" color="smMuted">
              {t("espd_question_count", { count: category.criteria.length })}
            </Text>
          </Box>
          <HStack spacing="3" flexShrink={0} alignSelf={{ base: "flex-end", sm: "center" }}>
            <StatusPill tone={tone}>{status}</StatusPill>
            <Icon as={isOpen ? FiChevronDown : FiChevronRight} color="smMuted" />
          </HStack>
        </Flex>
      </Button>
      {isOpen && (
        <Box
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          px={{ base: "3", md: "4" }}
          py="3"
          bg="smEspdCategoryBg"
        >
          <Stack spacing="2">{children}</Stack>
        </Box>
      )}
    </Box>
  );
};

const CriterionSection = ({
  category,
  criterion,
  isOpen,
  onToggle,
}: {
  category: BuyerPublicBidEspdCategoryDto;
  criterion: BuyerPublicBidEspdCriterionDto;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const tone = answerTone(criterion.answer);
  const criterionName = localizedText(i18n.language, criterion.nameSource, criterion.nameTranslation, criterion.name);
  const criterionDescription = localizedText(
    i18n.language,
    criterion.descriptionSource,
    criterion.descriptionTranslation,
    criterion.description,
  );
  const buttonId = criterionElementId(category, criterion);
  const panelId = criterionPanelId(category, criterion);

  return (
    <Box
      border="1px solid"
      borderColor="smBorder"
      borderRadius="md"
      overflow="hidden"
      bg="smEspdCriterionBg"
    >
      <Button
        id={buttonId}
        type="button"
        variant="unstyled"
        h="auto"
        w="full"
        display="block"
        textAlign="left"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        _focusVisible={{ boxShadow: "smFocusRing", zIndex: 1 }}
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", sm: "minmax(0, 1fr) auto" }}
          alignItems={{ base: "stretch", sm: "center" }}
          gap="3"
          px={{ base: "3", md: "4" }}
          py="3"
          bg="smEspdCriterionBg"
        >
          <Box minW="0">
            <Text fontWeight="bold" color="smText" noOfLines={{ base: 3, md: 2 }} overflowWrap="anywhere">
              {criterionName}
            </Text>
            {criterionDescription && (
              <Text mt="0.5" fontSize="sm" color="smMuted" noOfLines={{ base: 3, md: 2 }} overflowWrap="anywhere">
                {criterionDescription}
              </Text>
            )}
          </Box>
          <HStack spacing="3" justifySelf="end" alignSelf={{ base: "center", sm: "center" }}>
            <StatusPill tone={tone}>{answerLabel(criterion.answer, t)}</StatusPill>
            <Icon as={isOpen ? FiChevronDown : FiChevronRight} color="smMuted" />
          </HStack>
        </Box>
      </Button>
      {isOpen && (
        <Stack
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          spacing="4"
          px={{ base: "3", md: "4" }}
          py="4"
          bg="smBackground"
          borderTop="1px solid"
          borderColor="smBorder"
        >
          {criterion.responseGroups.length === 0 ? (
            <MutedValue>{t("No answer details")}</MutedValue>
          ) : (
            criterion.responseGroups.map((group) => <RequirementGroup key={group.id} group={group} />)
          )}
        </Stack>
      )}
    </Box>
  );
};

const RequirementGroup = ({ group }: { group: BuyerPublicBidEspdRequirementGroupDto }) => {
  const { t } = useTranslation();
  const showInstanceLabel = group.repeatable || group.instances.length > 1;

  return (
    <Stack spacing="3">
      {group.instances.map((instance, index) => (
        <Box
          key={`${group.id}-${instance.instanceIndex ?? index}`}
          pt={showInstanceLabel ? "3" : "0"}
          borderTop={showInstanceLabel && index > 0 ? "1px solid" : undefined}
          borderColor="smBorder"
        >
          {showInstanceLabel && (
            <Text fontSize="xs" fontWeight="bold" color="smMuted" textTransform="uppercase" mb="3">
              {t("Response {{number}}", { number: index + 1 })}
            </Text>
          )}
          <Stack spacing="0" divider={<Box borderTop="1px solid" borderColor="smBorder" />}>
            {instance.requirements.map((requirement) => (
              <RequirementRow key={`${requirement.id}-${instance.instanceIndex ?? "single"}`} requirement={requirement} />
            ))}
            {instance.subGroups.map((subGroup) => (
              <RequirementGroup key={subGroup.id} group={subGroup} />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const RequirementRow = ({ requirement }: { requirement: BuyerPublicBidEspdRequirementDto }) => (
  <LocalizedRequirementRow requirement={requirement} />
);

const LocalizedRequirementRow = ({ requirement }: { requirement: BuyerPublicBidEspdRequirementDto }) => {
  const { t, i18n } = useTranslation();
  const label = buyerFacingRequirementLabel(
    localizedText(i18n.language, requirement.labelSource, requirement.labelTranslation, requirement.label),
    t,
  );

  return (
    <Box py="3">
      <Text fontSize="sm" fontWeight="semibold" color="smMuted" lineHeight="short" overflowWrap="anywhere">
        {label}
      </Text>
      <Box minW="0" mt="1">
        <RequirementValue requirement={requirement} />
      </Box>
    </Box>
  );
};

const RequirementValue = ({ requirement }: { requirement: BuyerPublicBidEspdRequirementDto }) => {
  const { t, i18n } = useTranslation();
  const value = requirement.value;

  if (!value || isEmptyValue(value)) {
    return <MutedValue>{t("Not answered")}</MutedValue>;
  }

  switch (requirement.responseDataType) {
    case EspdResponseDataType.Indicator:
      return <IndicatorValue value={value.indicator} />;
    case EspdResponseDataType.Description:
      return value.description ? (
        <Text color="smText" lineHeight="tall" whiteSpace="pre-wrap" maxW="82ch">
          {value.description}
        </Text>
      ) : (
        <MutedValue>{t("Not answered")}</MutedValue>
      );
    case EspdResponseDataType.Date:
      return <MetricValue value={value.date ? formatDate(value.date, i18n.language) : null} />;
    case EspdResponseDataType.Amount:
      return <MetricValue value={formatAmount(value.amount, value.currencyId, i18n.language)} mono />;
    case EspdResponseDataType.CodeCountry:
      return <MetricValue value={formatCountryCode(value.code, i18n.language)} />;
    case EspdResponseDataType.Code:
      return <MetricValue value={value.code ?? null} />;
    case EspdResponseDataType.Percentage:
      return <PercentageValue value={value.percentage} />;
    case EspdResponseDataType.Period:
      return <MetricValue value={formatPeriod(value.period, i18n.language)} />;
    case EspdResponseDataType.QuantityInteger:
    case EspdResponseDataType.Quantity:
      return <MetricValue value={formatQuantity(value.quantity, value.quantityUnitCode, i18n.language)} />;
    case EspdResponseDataType.QuantityYear:
      return <MetricValue value={formatYear(value.quantity, i18n.language)} mono />;
    case EspdResponseDataType.EvidenceUrl:
      return <EvidenceValue value={value} />;
    default:
      return <FallbackValue value={value} />;
  }
};

const IndicatorValue = ({ value }: { value?: boolean | null }) => {
  const { t } = useTranslation();
  if (value == null) return <MutedValue>{t("Not answered")}</MutedValue>;
  return <StatusPill tone="neutral">{value ? t("Yes") : t("No")}</StatusPill>;
};

const MetricValue = ({ value, mono = false }: { value?: string | null; mono?: boolean }) => {
  const { t } = useTranslation();
  if (!value) return <MutedValue>{t("Not answered")}</MutedValue>;
  return (
    <Text color="smText" fontWeight="semibold" fontFamily={mono ? "mono" : undefined}>
      {value}
    </Text>
  );
};

const PercentageValue = ({ value }: { value?: number | null }) => {
  const { t, i18n } = useTranslation();
  if (value == null) return <MutedValue>{t("Not answered")}</MutedValue>;
  const bounded = Math.max(0, Math.min(100, value));
  return (
    <Box maxW="220px">
      <Text color="smText" fontWeight="semibold">
        {`${formatNumber(value, i18n.language)} %`}
      </Text>
      <Progress mt="2" value={bounded} size="xs" colorScheme="teal" borderRadius="full" />
    </Box>
  );
};

const EvidenceValue = ({ value }: { value: BuyerPublicBidEspdRequirementValueDto }) => {
  const { t } = useTranslation();
  const safeHref = safeExternalHref(value.evidenceUrl);

  if (!value.evidenceUrl) {
    if (value.evidenceName) {
      return (
        <Text color="smText" fontWeight="semibold" overflowWrap="anywhere">
          {value.evidenceName}
        </Text>
      );
    }
    return <MutedValue>{t("Not answered")}</MutedValue>;
  }

  if (!safeHref) {
    return (
      <Text color="smText" fontWeight="semibold" overflowWrap="anywhere">
        {value.evidenceName || value.evidenceUrl}
      </Text>
    );
  }

  return (
    <Link href={safeHref} isExternal color="smPrimary" fontWeight="semibold">
      <HStack as="span" spacing="1.5" align="center">
        <Text as="span" overflowWrap="anywhere">
          {value.evidenceName || formatEvidenceUrl(value.evidenceUrl)}
        </Text>
        <Icon as={FiExternalLink} boxSize="3.5" />
      </HStack>
    </Link>
  );
};

const FallbackValue = ({ value }: { value: BuyerPublicBidEspdRequirementValueDto }) => {
  const { t, i18n } = useTranslation();
  const candidates = [
    value.description,
    value.date,
    formatAmount(value.amount, value.currencyId, i18n.language),
    value.code,
    value.percentage != null ? `${formatNumber(value.percentage, i18n.language)} %` : null,
    formatQuantity(value.quantity, value.quantityUnitCode, i18n.language),
    value.period,
    value.evidenceName,
    value.evidenceUrl,
  ].filter(Boolean);

  if (candidates.length === 0) {
    return <MutedValue>{t("Not answered")}</MutedValue>;
  }

  return (
    <Stack spacing="1">
      {candidates.map((candidate) => (
        <Text key={candidate} color="smText" fontWeight="semibold">
          {candidate}
        </Text>
      ))}
    </Stack>
  );
};

const MutedValue = ({ children }: { children: ReactNode }) => (
  <Text color="smMuted" fontStyle="italic">
    {children}
  </Text>
);

const StatusPill = ({ tone, children }: { tone: Tone; children: ReactNode }) => (
  <Badge
    bg={toneBg(tone)}
    color={toneText(tone)}
    border="1px solid"
    borderColor={toneBorder(tone)}
    px="2.5"
    py="1"
    borderRadius="full"
    textTransform="none"
    fontWeight="bold"
    whiteSpace="nowrap"
  >
    <HStack spacing="1.5">
      {tone === "ok" && <Icon as={FiCheckCircle} boxSize="3" />}
      <Text as="span">{children}</Text>
    </HStack>
  </Badge>
);

const firstIssue = (category: BuyerPublicBidEspdCategoryDto) =>
  category.criteria.find(
    (criterion) =>
      criterion.answer === BuyerPublicBidEspdAnswerDto.Deviation || criterion.answer === BuyerPublicBidEspdAnswerDto.Unanswered,
  );

const categoryHasIssue = (category: BuyerPublicBidEspdCategoryDto) => Boolean(firstIssue(category));

const espdStatus = (
  deviationCount: number,
  unansweredCount: number,
  t: (value: string, options?: { count: number }) => string,
): { label: string; tone: Tone } => {
  if (deviationCount > 0) {
    return { label: t("espd_deviation_count", { count: deviationCount }), tone: "issue" };
  }
  if (unansweredCount > 0) {
    return { label: t("espd_unanswered_count", { count: unansweredCount }), tone: "neutral" };
  }
  return { label: t("OK"), tone: "ok" };
};

const answerTone = (answer: BuyerPublicBidEspdAnswerDto): Tone => {
  if (answer === BuyerPublicBidEspdAnswerDto.Deviation) return "issue";
  if (answer === BuyerPublicBidEspdAnswerDto.Unanswered) return "neutral";
  return "ok";
};

const answerLabel = (answer: BuyerPublicBidEspdAnswerDto, t: (value: string) => string) => {
  if (answer === BuyerPublicBidEspdAnswerDto.Deviation) return t("Deviation");
  if (answer === BuyerPublicBidEspdAnswerDto.Unanswered) return t("Not answered");
  return t("OK");
};

// Keep category keys visible to i18next-parser:
// t("espd_category_EXCLUSION_CONVICTIONS") t("espd_category_EXCLUSION_CONTRIBUTIONS")
// t("espd_category_EXCLUSION_SOCIAL") t("espd_category_EXCLUSION_BUSINESS")
// t("espd_category_EXCLUSION_MISCONDUCT") t("espd_category_EXCLUSION_CONFLICT")
// t("espd_category_EXCLUSION_NATIONAL")
const CATEGORY_LABEL_KEYS: Record<string, string> = {
  EXCLUSION_CONVICTIONS: "espd_category_EXCLUSION_CONVICTIONS",
  EXCLUSION_CONTRIBUTIONS: "espd_category_EXCLUSION_CONTRIBUTIONS",
  EXCLUSION_SOCIAL: "espd_category_EXCLUSION_SOCIAL",
  EXCLUSION_BUSINESS: "espd_category_EXCLUSION_BUSINESS",
  EXCLUSION_MISCONDUCT: "espd_category_EXCLUSION_MISCONDUCT",
  EXCLUSION_CONFLICT: "espd_category_EXCLUSION_CONFLICT",
  EXCLUSION_NATIONAL: "espd_category_EXCLUSION_NATIONAL",
};

const espdCategoryName = (category: BuyerPublicBidEspdCategoryDto, t: TFunction) => {
  const key = CATEGORY_LABEL_KEYS[category.code];
  return key ? t(key) : category.name;
};

const localizedText = (
  language: string,
  source?: string | null,
  translation?: string | null,
  fallback?: string | null,
) => {
  const isNorwegian = localeFor(language).startsWith("nb");
  const preferred = isNorwegian ? translation : source;
  return preferred?.trim() || fallback || "";
};

const buyerFacingRequirementLabel = (label: string, t: TFunction) => {
  const normalized = label.trim().toLocaleLowerCase();
  if (normalized === "your answer?" || normalized === "ditt svar?") {
    return t("Supplier's answer");
  }
  return label;
};

const categoryElementId = (category: BuyerPublicBidEspdCategoryDto) => `espd-category-${safeId(category.code)}`;
const categoryButtonId = (category: BuyerPublicBidEspdCategoryDto) => `espd-category-button-${safeId(category.code)}`;
const categoryPanelId = (category: BuyerPublicBidEspdCategoryDto) => `espd-category-panel-${safeId(category.code)}`;

const criterionElementId = (category: BuyerPublicBidEspdCategoryDto, criterion: BuyerPublicBidEspdCriterionDto) =>
  `espd-criterion-${safeId(category.code)}-${safeId(criterion.id)}`;

const criterionPanelId = (category: BuyerPublicBidEspdCategoryDto, criterion: BuyerPublicBidEspdCriterionDto) =>
  `espd-criterion-panel-${safeId(category.code)}-${safeId(criterion.id)}`;

const criterionKey = (category: BuyerPublicBidEspdCategoryDto, criterion: BuyerPublicBidEspdCriterionDto) =>
  `${category.code}:${criterion.id}`;

const safeId = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, "_");

const scrollToElement = (id: string) => {
  const scroll = () => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
    element?.focus({ preventScroll: true });
  };

  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(scroll);
  } else {
    window.setTimeout(scroll, 0);
  }
};

const addOnce = (values: string[], value: string) => (values.includes(value) ? values : [...values, value]);

const isEmptyValue = (value: BuyerPublicBidEspdRequirementValueDto) =>
  value.indicator == null &&
  !value.description &&
  !value.date &&
  value.amount == null &&
  !value.currencyId &&
  !value.code &&
  value.percentage == null &&
  value.quantity == null &&
  !value.quantityUnitCode &&
  !value.period &&
  !value.evidenceUrl &&
  !value.evidenceName;

const formatDate = (value: string, language: string) => {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(localeFor(language), { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(
    date,
  );
};

const formatPeriod = (value: string | null | undefined, language: string) => {
  if (!value) return null;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})\s*(?:\/|\s+-\s+)\s*(\d{4}-\d{2}-\d{2})$/);
  if (!match) return value;
  const [, start, end] = match;
  return `${formatDate(start, language)} - ${formatDate(end, language)}`;
};

const formatAmount = (amount?: number | null, currencyId?: string | null, language = "en") => {
  if (amount == null) return null;
  return currencyId ? `${formatNumber(amount, language)} ${currencyId}` : formatNumber(amount, language);
};

const formatCountryCode = (code?: string | null, language = "en") => {
  if (!code) return null;
  const normalizedCode = code.toUpperCase();
  try {
    const displayNames = new Intl.DisplayNames([localeFor(language)], { type: "region" });
    const countryName = displayNames.of(normalizedCode);
    return countryName ? `${normalizedCode} · ${countryName}` : code;
  } catch {
    return code;
  }
};

const formatQuantity = (quantity?: number | null, unit?: string | null, language = "en") => {
  if (quantity == null) return null;
  return unit ? `${formatNumber(quantity, language)} ${formatUnitCode(unit, quantity, language)}` : formatNumber(quantity, language);
};

const formatUnitCode = (unit: string, quantity: number, language = "en") => {
  const normalizedUnit = unit.toUpperCase();
  const isNorwegian = localeFor(language).startsWith("nb");
  const unitLabels: Record<string, { en: [string, string]; no: [string, string] }> = {
    C62: { en: ["unit", "units"], no: ["stk.", "stk."] },
    DAY: { en: ["day", "days"], no: ["dag", "dager"] },
    HUR: { en: ["hour", "hours"], no: ["time", "timer"] },
    KGM: { en: ["kg", "kg"], no: ["kg", "kg"] },
    MON: { en: ["month", "months"], no: ["måned", "måneder"] },
  };
  const labels = unitLabels[normalizedUnit];
  if (!labels) return unit;
  const [singular, plural] = isNorwegian ? labels.no : labels.en;
  return quantity === 1 ? singular : plural;
};

const formatEvidenceUrl = (value?: string | null) => {
  if (!value) return "";
  try {
    const url = new URL(value);
    return `${url.host}${url.pathname}`.replace(/\/$/, "");
  } catch {
    return value;
  }
};

const safeExternalHref = (value?: string | null) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
};

const formatYear = (quantity: number | null | undefined, language = "en") => {
  if (quantity == null) return null;
  return new Intl.NumberFormat(localeFor(language), { maximumFractionDigits: 0, useGrouping: false }).format(quantity);
};

const formatNumber = (value: number, language = "en") =>
  new Intl.NumberFormat(localeFor(language), { maximumFractionDigits: 2 }).format(value).replace(/\u00a0/g, " ");

const localeFor = (language: string) => {
  const normalized = language?.toLowerCase();
  return normalized?.startsWith("nb") || normalized?.startsWith("no") ? "nb-NO" : "en-GB";
};

const toneBg = (tone: Tone) => {
  if (tone === "issue") return "smStatusIssueBg";
  if (tone === "neutral") return "smStatusNeutralBg";
  return "smStatusOkBg";
};

const toneText = (tone: Tone) => {
  if (tone === "issue") return "smStatusIssueText";
  if (tone === "neutral") return "smStatusNeutralText";
  return "smStatusOkText";
};

const toneBorder = (tone: Tone) => {
  if (tone === "issue") return "smStatusIssueBorder";
  if (tone === "neutral") return "smStatusNeutralBorder";
  return "smStatusOkBorder";
};

const toneAccent = (tone: Tone) => {
  if (tone === "issue") return "smStatusIssueAccent";
  if (tone === "neutral") return "smStatusNeutralAccent";
  return "smStatusOkAccent";
};
